/**
 * AddressNormalizerL1.ts
 * Normalización conservadora de direcciones (Nivel 1)
 *
 * Transformaciones L1 (conservadoras, no destructivas):
 * 1. Corrección mojibake UTF-8 en direcciones
 * 2. Uppercase
 * 3. Normalización diacríticos (NFD + strip)
 * 4. Expansión abreviaturas viales (C/ → CALLE, Avda → AVENIDA, etc.)
 * 5. Normalización numerales (nº, num, núm → N)
 * 6. Colapso de espacios múltiples
 * 7. Strip de puntuación redundante
 *
 * Diseñada para mejorar matching pre-geocodificación sin perder
 * información semántica relevante.
 *
 * @module AddressNormalizerL1
 * @version 1.0.0
 * @date Febrero 2026
 */

import { fixMojibake } from '../../lib/EncodingCorrector';
import { expandTipoVia, initTipoViaPatterns } from '../data/tipoViaMap';

// ============================================================================
// TIPOS
// ============================================================================

export interface NormalizeL1Result {
  /** Dirección normalizada */
  normalized: string;
  /** Dirección original */
  original: string;
  /** Si se modificó el texto */
  wasModified: boolean;
  /** Transformaciones aplicadas en orden */
  transforms: NormalizeL1Transform[];
}

export type NormalizeL1Transform =
  | 'MOJIBAKE_FIX'
  | 'UPPERCASE'
  | 'DIACRITICS_STRIP'
  | 'TIPO_VIA_EXPAND'
  | 'NUMERAL_NORMALIZE'
  | 'SPACE_COLLAPSE'
  | 'PUNCTUATION_CLEAN';

// ============================================================================
// PATRONES ESTÁTICOS
// ============================================================================

/**
 * Patrones para normalizar numerales de portal/número.
 * nº, Nº, n°, num, núm, numero → N
 * Se aplican DESPUÉS de uppercase y strip diacríticos.
 */
const NUMERAL_PATTERNS: Array<{ regex: RegExp; replacement: string }> = [
  // "Nº 5", "N° 5", "Nº5" → "N 5"
  { regex: /\bN[º°]\s*/g, replacement: 'N ' },
  // "NUM.", "NUM ", "NÚM." → "N "  (ya en uppercase+sin acentos = NUM)
  { regex: /\bNUM\.?\s*/g, replacement: 'N ' },
  // "S/N" → "SN" (sin número)
  { regex: /\bS\s*\/\s*N\b/g, replacement: 'SN' },
];

/**
 * Puntuación redundante que se limpia sin perder información.
 * Conserva "/" cuando forma parte de abreviaturas ya procesadas.
 */
const PUNCTUATION_CLEAN_REGEX = /[.,;:()[\]{}'"¿?¡!]+/g;

// ============================================================================
// FUNCIONES PRIVADAS
// ============================================================================

/**
 * Elimina diacríticos conservando la letra base.
 * "Ávila" → "AVILA", "José" → "JOSE"
 */
function stripDiacritics(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Normaliza numerales de portal/número.
 */
function normalizeNumerals(text: string): string {
  let result = text;
  for (const { regex, replacement } of NUMERAL_PATTERNS) {
    result = result.replace(regex, replacement);
  }
  return result;
}

/**
 * Colapsa espacios múltiples y limpia bordes.
 */
function collapseSpaces(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

// ============================================================================
// API PÚBLICA
// ============================================================================

/**
 * Normalización conservadora L1 de una dirección.
 *
 * @param address Dirección original (puede tener mojibake, abreviaturas, etc.)
 * @returns Resultado con dirección normalizada y metadatos
 *
 * @example
 * normalizeAddressL1("C/ Iglesia, 5")
 * // → { normalized: "CALLE IGLESIA N 5", wasModified: true, ... }
 *
 * @example
 * normalizeAddressL1("Avda. de AndalucÃ­a, nº 12")
 * // → { normalized: "AVENIDA DE ANDALUCIA N 12", wasModified: true, ... }
 */
export function normalizeAddressL1(address: string): NormalizeL1Result {
  const original = address;
  const transforms: NormalizeL1Transform[] = [];

  if (!address || !address.trim()) {
    return { normalized: '', original, wasModified: false, transforms };
  }

  // Asegurar patrones inicializados
  initTipoViaPatterns();

  let current = address.trim();

  // 1. Corrección mojibake UTF-8
  const mojibakeFixed = fixMojibake(current);
  if (mojibakeFixed !== current) {
    transforms.push('MOJIBAKE_FIX');
    current = mojibakeFixed;
  }

  // 2. Uppercase
  const uppercased = current.toUpperCase();
  if (uppercased !== current) {
    transforms.push('UPPERCASE');
    current = uppercased;
  }

  // 3. Strip diacríticos
  const stripped = stripDiacritics(current);
  if (stripped !== current) {
    transforms.push('DIACRITICS_STRIP');
    current = stripped;
  }

  // 4. Expansión tipo de vía (usa expandTipoVia que ya normaliza el nombre)
  const expanded = expandTipoVia(current);
  if (expanded !== current) {
    transforms.push('TIPO_VIA_EXPAND');
    current = expanded;
  }

  // 5. Normalización de numerales
  const numeralized = normalizeNumerals(current);
  if (numeralized !== current) {
    transforms.push('NUMERAL_NORMALIZE');
    current = numeralized;
  }

  // 6. Limpieza puntuación redundante
  const cleaned = current.replace(PUNCTUATION_CLEAN_REGEX, ' ');
  if (cleaned !== current) {
    transforms.push('PUNCTUATION_CLEAN');
    current = cleaned;
  }

  // 7. Colapso espacios (siempre al final)
  const collapsed = collapseSpaces(current);
  if (collapsed !== current) {
    transforms.push('SPACE_COLLAPSE');
    current = collapsed;
  }

  return {
    normalized: current,
    original,
    wasModified: current !== original.trim(),
    transforms,
  };
}

/**
 * Versión simplificada que solo devuelve el string normalizado.
 *
 * @param address Dirección original
 * @returns Dirección normalizada L1
 */
export function normalizeL1(address: string): string {
  return normalizeAddressL1(address).normalized;
}
